import { ChevronDown, ChevronRight, CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';

import type { WorkflowFailure } from '../types/workflow-inspection.types';

/** Flatten `failure → cause → …` into an ordered list (outermost first). */
function flattenFailure(failure: WorkflowFailure): WorkflowFailure[] {
  const chain: WorkflowFailure[] = [];
  let node: WorkflowFailure | null = failure;
  // Server caps depth (10 persisted / 32 Temporal); guard anyway against cycles.
  while (node && chain.length < 40) {
    chain.push(node);
    node = node.cause;
  }
  return chain;
}

/**
 * Renders a Temporal failure as the contract asks — `message → cause.message →
 * … → stackTrace`: each level's type and message, a non-retryable flag, and
 * the innermost available stack trace behind a toggle (it's long and mostly
 * for engineers). Values arrive already masked.
 */
export function FailureChain({ failure }: { failure: WorkflowFailure }) {
  const { t } = useTranslation('workflow-inspection');
  const [showTrace, setShowTrace] = useState(false);
  const chain = flattenFailure(failure);
  // Innermost trace is the one that points at the real fault.
  const trace = [...chain].reverse().find((f) => f.stackTrace)?.stackTrace ?? null;

  return (
    <div
      className="border-destructive/30 bg-destructive/5 space-y-2 rounded-lg border p-3"
      data-testid="workflow-inspector-failure-chain"
    >
      <ol className="space-y-2">
        {chain.map((level, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-sm"
            data-testid="workflow-inspector-failure-chain-level"
          >
            <CircleAlert aria-hidden="true" className="text-destructive mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              {i > 0 && (
                <span className="text-muted-foreground mr-1 text-xs">{t('failure.causedBy')}</span>
              )}
              {level.type && (
                <span className="text-foreground mr-1.5 font-mono text-xs font-semibold">
                  {level.type}
                </span>
              )}
              {level.nonRetryable && (
                <Badge variant="outline" className="mr-1.5 align-middle text-[10px]">
                  {t('failure.nonRetryable')}
                </Badge>
              )}
              <p className="text-foreground break-words whitespace-pre-wrap">
                {level.message ?? t('failure.noMessage')}
              </p>
            </div>
          </li>
        ))}
      </ol>

      {trace && (
        <div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 cursor-pointer gap-1 px-2 text-xs"
              aria-expanded={showTrace}
              data-testid="workflow-inspector-failure-chain-stack-trace-toggle"
              onClick={() => {
                setShowTrace(!showTrace);
              }}
            >
              {showTrace ? (
                <ChevronDown aria-hidden="true" className="size-3.5" />
              ) : (
                <ChevronRight aria-hidden="true" className="size-3.5" />
              )}
              {showTrace ? t('failure.hideStackTrace') : t('failure.showStackTrace')}
            </Button>
            {showTrace && <CopyIconButton text={trace} label={t('failure.stackTrace')} />}
          </div>
          {showTrace && (
            <pre
              data-testid="workflow-inspector-failure-chain-stack-trace"
              className="bg-background mt-1 max-h-72 overflow-auto rounded-md border p-3 font-mono text-xs whitespace-pre"
            >
              {trace}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
